import { type CalendarDate } from '@internationalized/date'
import { ChevronLeft, ChevronRight } from '@untitledui/icons'
import { Fragment, useContext, useState } from 'react'
import {
  Calendar as AriaCalendar,
  CalendarContext as AriaCalendarContext,
  CalendarGrid as AriaCalendarGrid,
  CalendarGridBody as AriaCalendarGridBody,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarHeaderCell as AriaCalendarHeaderCell,
  CalendarStateContext as AriaCalendarStateContext,
  Heading as AriaHeading,
  useSlottedContext
} from 'react-aria-components'

import Button from '@components/designSystem/Button'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'
import { cx } from '@utils/cx'

import CalendarCell from './cell'
import { DateInput } from './date-input'

import type { HTMLAttributes, PropsWithChildren } from 'react'
import type { CalendarProps as AriaCalendarProps, DateValue } from 'react-aria-components'

const CalendarContextProvider = ({ children }: PropsWithChildren) => {
  const [value, onChange] = useState<DateValue | null>(null)
  const [focusedValue, onFocusChange] = useState<DateValue | undefined>()

  return (
    <AriaCalendarContext.Provider
      value={{ value, onChange, focusedValue, onFocusChange } as AriaCalendarProps<DateValue, 'single' | 'multiple'>}
    >
      {children}
    </AriaCalendarContext.Provider>
  )
}

interface PresetButtonProps extends HTMLAttributes<HTMLButtonElement> {
    value: CalendarDate
    onClick?: () => void
}

const PresetButton = ({ value, onClick, children, ...props }: PresetButtonProps) => {
  const context = useContext(AriaCalendarStateContext)

  if (!context) {
    throw new Error('Preset must be used within a Calendar component')
  }

  const handleClick = () => {
    context.setValue(value)
    context.setFocusedDate(value)
    onClick?.()
  }

  return (
    <Button
      {...props}
      // It's important to give `null` explicitly to the `slot` prop
      // otherwise the button will throw an error due to not using one of
      // the required slots inside the Calendar component.
      // Passing `null` will tell the button to not use a slot context.
      slot={null}
      size="md"
      variant="outline"
      onClick={handleClick}
    >
      {children}
    </Button>
  )
}

export interface CalendarProps extends AriaCalendarProps<DateValue> {
    /** The dates to highlight. */
    showInput?: boolean
    highlightedDates?: DateValue[]
    preset?: { label: string; value: CalendarDate; onClick?: () => void }
    size?: 'md' | 'lg'
}

export const Calendar = ({ highlightedDates, className, showInput, preset, size = 'md', ...props }: CalendarProps) => {
  const { t } = useTypedTranslation()
  const context = useSlottedContext(AriaCalendarContext)

  const ContextWrapper = context ? Fragment : CalendarContextProvider

  return (
    <ContextWrapper>
      <AriaCalendar {...props} className={(state) => cx('flex flex-col gap-3', typeof className === 'function' ? className(state) : className)}>
        <header className="flex items-center justify-between">
          <Button slot="previous" iconLeading={<ChevronLeft />} size="sm" variant="ghost" className="size-8" aria-label={t('common.datepicker.previousMonth')} />
          <AriaHeading className="text-sm font-semibold text-fg-secondary" />
          <Button slot="next" iconLeading={<ChevronRight />} size="sm" variant="ghost" className="size-8" aria-label={t('common.datepicker.nextMonth')} />
        </header>

        { (showInput ?? preset) && (
          <div className={cx('flex gap-3', showInput && preset ? 'justify-between' : 'justify-center')}>
            {showInput && <DateInput className="flex-1" />}
            {preset && <PresetButton value={preset.value} onClick={preset.onClick}>{preset.label}</PresetButton>}
          </div>
        )}

        <AriaCalendarGrid weekdayStyle="short" className="w-max">
          <AriaCalendarGridHeader className="border-b-4 border-transparent">
            {(day) => (
              <AriaCalendarHeaderCell className="p-0">
                <div className="flex size-10 items-center justify-center text-sm font-medium text-secondary">{day.slice(0, 2)}</div>
              </AriaCalendarHeaderCell>
            )}
          </AriaCalendarGridHeader>
          <AriaCalendarGridBody className={`
            [&_td]:p-0
            [&_tr]:border-b-4 [&_tr]:border-transparent
            [&_tr:last-of-type]:border-none
          `}>
            {(date) => (
              <CalendarCell size={size} date={date} isHighlighted={highlightedDates?.some((highlightedDate) => date.compare(highlightedDate) === 0)} />
            )}
          </AriaCalendarGridBody>
        </AriaCalendarGrid>
      </AriaCalendar>
    </ContextWrapper>
  )
}
